import { ButtonHTMLAttributes } from "react";
import { cx } from "./classNames.js";

export type IconButtonVariant = "ghost" | "secondary";
export type IconButtonSize = "xs" | "sm" | "md";

const VARIANT_CLASSES: Record<IconButtonVariant, string> = {
  ghost:
    "bg-transparent text-slate-400 hover:bg-slate-800 hover:text-white focus-visible:ring-slate-500",
  secondary:
    "bg-slate-700 text-slate-100 hover:bg-slate-600 focus-visible:ring-slate-400",
};

const SIZE_CLASSES: Record<IconButtonSize, string> = {
  // Dense rows (process tree, view builder) where sm would crowd the line.
  xs: "h-5 w-5",
  sm: "h-6 w-6",
  md: "h-8 w-8",
};

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: IconButtonVariant;
  readonly size?: IconButtonSize;
}

/**
 * Square icon-only chrome control — DESIGN-SYSTEM.md §1 ("square icon targets
 * are app-shell chrome conventions"). Same base + mandatory focus ring +
 * `rounded-md` as {@link Button}, sized to a square touch target. Always give
 * it an `aria-label` — there's no text child to name it.
 */
export function IconButton({
  variant = "ghost",
  size = "md",
  className,
  children,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type="button"
      className={cx(
        "inline-flex items-center justify-center rounded-md",
        "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950",
        "disabled:cursor-not-allowed disabled:opacity-50",
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
